import React, { useState, useRef } from "react";
import Modal from "react-modal";
import { FaTimes, FaWallet, FaExclamationCircle, FaCloudUploadAlt, FaEye } from "react-icons/fa";
import { WalletApiService } from "../../../../../service/seller/walletApiService";
import { ToastTypes, useMessage } from "../../../../../base/context/MessageProvider";
import { NetworkConfiguration } from "../../../../../network/networkConfriguation";
import { getAuthLocalStorage } from "../../../../../storage/localStorage";
import "./style.css";

const customStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 10000,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    background: "transparent",
    padding: 0,
    borderRadius: "20px",
    maxWidth: "580px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "visible",
  },
};

Modal.setAppElement("#root");

export default function RechargeRequestModal({ modalIsOpen, setIsOpen, handleRefresh }) {
  const [transactionId, setTransactionId] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofUrl, setPaymentProofUrl] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const message = useMessage();

  function closeModal() {
    if (!isSubmitting) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setIsOpen(false);
      setTransactionId("");
      setTransactionDate("");
      setAmount("");
      setReason("");
      setPaymentProofFile(null);
      setPaymentProofUrl("");
      setPreviewUrl(null);
      setError("");
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Please upload an image (JPEG, PNG or WebP)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }
      setPaymentProofFile(file);
      setPaymentProofUrl("");
      setError("");
    }
  };

  const removeFile = () => {
    setPaymentProofFile(null);
    setPaymentProofUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadImage = async () => {
    if (!paymentProofFile) return null;
    const formData = new FormData();
    formData.append("image", paymentProofFile);

    const loginHeader = getAuthLocalStorage()?.loginHeader;
    const headers = { Authorization: `Bearer ${loginHeader}` };

    const res = await fetch(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.UPLOAD_RECHARGE_PROOF,
      {
        method: "POST",
        headers,
        body: formData,
      }
    );
    const data = await res.json();
    if (data?.success && data?.url) return data.url;
    throw new Error(data?.message || "Upload failed");
  };

  const validate = () => {
    setError("");
    if (!transactionId?.trim()) {
      setError("Transaction ID is required");
      return false;
    }
    if (!transactionDate) {
      setError("Transaction date is required");
      return false;
    }
    if (!paymentProofFile && !paymentProofUrl) {
      setError("Payment proof screenshot is required");
      return false;
    }
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      setError("Minimum amount is ₹1");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      let proofUrl = paymentProofUrl;
      if (paymentProofFile) {
        proofUrl = await uploadImage();
      }

      const payload = {
        transactionId: transactionId.trim(),
        transactionDate: new Date(transactionDate).toISOString(),
        paymentProofUrl: proofUrl,
        amount: parseFloat(amount),
        message: reason?.trim() || undefined,
      };

      const res = await WalletApiService.createRechargeRequest(payload);
      setIsSubmitting(false);

      if (res?.status?.code === 200) {
        message.showToast(res.status.message || "Recharge request submitted successfully", ToastTypes.SUCCESS);
        closeModal();
        handleRefresh?.();
      } else {
        message.showToast(res?.error?.message || "Failed to submit request", ToastTypes.ERROR);
      }
    } catch (err) {
      setIsSubmitting(false);
      message.showToast(err?.message || "Failed to submit request", ToastTypes.ERROR);
    }
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Request Recharge"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
    >
      <div className="recharge-request-modal-container">
        <div className="recharge-request-modal-header">
          <h2 className="recharge-request-modal-title">
            <FaWallet className="recharge-request-modal-title-icon" />
            Request Recharge (Admin Approval)
          </h2>
          <button
            type="button"
            onClick={closeModal}
            className="recharge-request-modal-close"
            disabled={isSubmitting}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="recharge-request-modal-body">
            <p className="recharge-request-modal-description">
              Fill in your payment details. Admin will review and approve your request. Amount will be added to your IM Cash wallet after approval.
            </p>

            <div className="recharge-request-modal-form-row">
              <div className="recharge-request-modal-form-group">
                <label className="recharge-request-modal-label">
                  Transaction ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`recharge-request-modal-input ${error && !transactionId ? "error" : ""}`}
                  value={transactionId}
                  onChange={(e) => { setTransactionId(e.target.value); setError(""); }}
                  placeholder="e.g. TXN123456789"
                  disabled={isSubmitting}
                />
              </div>
              <div className="recharge-request-modal-form-group">
                <label className="recharge-request-modal-label">
                  Transaction Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className={`recharge-request-modal-input ${error && !transactionDate ? "error" : ""}`}
                  value={transactionDate}
                  onChange={(e) => { setTransactionDate(e.target.value); setError(""); }}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="recharge-request-modal-form-row">
              <div className="recharge-request-modal-form-group">
                <label className="recharge-request-modal-label">
                  Amount (₹) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={`recharge-request-modal-input ${error && (!amount || parseFloat(amount) <= 0) ? "error" : ""}`}
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(""); }}
                  placeholder="Enter amount"
                  disabled={isSubmitting}
                />
                <div className="recharge-request-modal-hint">Minimum: ₹1</div>
              </div>
              <div className="recharge-request-modal-form-group">
                <label className="recharge-request-modal-label">
                  Payment Proof (Screenshot) <span className="required">*</span>
                </label>
              {paymentProofFile ? (
                <div className="recharge-request-modal-file-row">
                  <span className="recharge-request-modal-file-name" title={paymentProofFile.name}>{paymentProofFile.name}</span>
                  <div className="recharge-request-modal-file-actions">
                    <button
                      type="button"
                      className="recharge-request-modal-view-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        if (previewUrl) URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(URL.createObjectURL(paymentProofFile));
                      }}
                    >
                      <FaEye /> View Image
                    </button>
                    <button type="button" className="recharge-request-modal-remove-file" onClick={removeFile}>
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`recharge-request-modal-file-zone ${error ? "error" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <FaCloudUploadAlt className="recharge-request-modal-file-icon" />
                  <span>Click to upload (JPEG, PNG, WebP - max 5MB)</span>
                </div>
              )}
              </div>
            </div>

            <div className="recharge-request-modal-form-group">
              <label className="recharge-request-modal-label">
                Message / Reason (optional)
              </label>
              <textarea
                className="recharge-request-modal-input recharge-request-modal-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. why you need this recharge or any note for admin"
                rows={2}
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <div className="recharge-request-modal-error">
                <FaExclamationCircle className="recharge-request-modal-error-icon" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="recharge-request-modal-footer">
            <button
              type="button"
              onClick={closeModal}
              className="recharge-request-modal-btn recharge-request-modal-btn-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`recharge-request-modal-btn recharge-request-modal-btn-submit ${isSubmitting ? "loading" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="recharge-request-modal-spinner" aria-hidden />
                  Submitting req…
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>

        {/* Image preview modal */}
        {previewUrl && (
          <div className="recharge-request-preview-overlay" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
            <div className="recharge-request-preview-box" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="recharge-request-preview-close" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
                <FaTimes />
              </button>
              <img src={previewUrl} alt="Payment proof" />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
